// client/src/hooks/useActivityTracking.js
import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/components/hooks/useAuth';
import { useVipBuyer } from '@/contexts/VipBuyerContext';
import { useLocation } from 'react-router-dom';
import activityTrackingService from '@/services/ActivityTrackingService';
import { getLogger } from '@/utils/logger';

const log = getLogger('useActivityTracking');


/**
 * Hook for tracking VIP buyer activity
 * This hook should be used at the app level to initialize tracking
 * and at the component level to track specific actions
 * 
 * @param {Object} options - Options for activity tracking
 * @param {boolean} [options.trackPageViews=true] - Whether to track page views automatically
 * @param {boolean} [options.trackClicks=true] - Whether to track clicks automatically
 * @returns {Object} Activity tracking methods
 */
export function useActivityTracking(options = {}) {
  const {
    trackPageViews = true,
    trackClicks = true
  } = options;

  const { isAuthenticated, identityUser, getToken } = useAuth();
  const { isVipBuyer, vipBuyerData } = useVipBuyer();
  const location = useLocation();
  const initialized = useRef(false);
  const prevPathname = useRef(location.pathname);

  // Initialize activity tracking when a VIP buyer logs in
  useEffect(() => {
    if (
      isAuthenticated && 
      isVipBuyer && 
      vipBuyerData && 
      vipBuyerData.id && 
      identityUser &&
      identityUser.sub &&
      !initialized.current
    ) {
      // Initialize the activity tracking service
      activityTrackingService.init({
        getToken,
        vipBuyerId: vipBuyerData.id,
        identityUserId: identityUser.sub
      });
      
      // Start tracking
      activityTrackingService.startTracking();
      initialized.current = true;
      
      log.info(
        '[useActivityTracking:initTracking] > [Request]: initTracking > [Response]: initialized'
      );
      
      // Cleanup when component unmounts
      return () => {
        if (initialized.current) {
          activityTrackingService.stopTracking();
          initialized.current = false;
        }
      };
    }
  }, [isAuthenticated, isVipBuyer, vipBuyerData, identityUser, getToken]);

  // Track page views when location changes
  useEffect(() => {
    if (
      initialized.current && 
      trackPageViews && 
      location.pathname !== prevPathname.current
    ) {
      activityTrackingService.recordPageView();
      prevPathname.current = location.pathname;
    }
  }, [location.pathname, trackPageViews]);

  // Callback functions for tracking specific actions
  
  /**
   * Track property view
   * @param {Object} property - Property data
   */
  const trackPropertyView = useCallback((property) => {
    if (initialized.current && property) {
      log.info(
        `[useActivityTracking:trackPropertyView] > [Request]: recordPropertyView > [Response]: queued > [Comment]: propertyId=${property?.id}`
      );
      activityTrackingService.recordPropertyView(property);
      
      // For debugging, also log the tracking state
      log.debug(
        `[useActivityTracking:trackPropertyView] > [Request]: trackingState > [Response]: debug > [Comment]: isVipBuyer=${isVipBuyer} initialized=${initialized.current} vipBuyerId=${vipBuyerData?.id || "unknown"} identityId=${identityUser?.sub || "unknown"}`
      );
    } else {
      log.warn(
        `[useActivityTracking:trackPropertyView] > [Request]: recordPropertyView > [Response]: skipped > [Comment]: initialized=${initialized.current} hasProperty=${!!property}`
      );
    }
  }, [isVipBuyer, vipBuyerData, identityUser]);

  /**
   * Track search
   * @param {string} query - Search query
   * @param {number} resultsCount - Number of results
   * @param {Object} filters - Search filters
   */
  const trackSearch = useCallback((query, resultsCount, filters = {}) => {
    if (initialized.current) {
      activityTrackingService.recordSearch(query, resultsCount, filters);
    }
  }, []);

  /**
   * Track offer submission
   * @param {Object} offerData - Offer data
   */
  const trackOfferSubmission = useCallback((offerData) => {
    if (initialized.current && offerData) {
      activityTrackingService.recordOfferSubmission(offerData);
    }
  }, []);

  /**
   * Track email interaction
   * @param {Object} emailData - Email interaction data
   */
  const trackEmailInteraction = useCallback((emailData) => {
    if (initialized.current && emailData) {
      activityTrackingService.recordEmailInteraction(emailData);
    }
  }, []);

  /**
   * Track custom event
   * @param {string} eventType - Type of event
   * @param {Object} eventData - Event data
   */
  const trackEvent = useCallback((eventType, eventData = {}) => {
    if (initialized.current) {
      activityTrackingService.recordEvent(eventType, eventData);
    }
  }, []);

  return {
    isTracking: initialized.current,
    trackPropertyView,
    trackSearch,
    trackOfferSubmission,
    trackEmailInteraction,
    trackEvent
  };
}

export default useActivityTracking;
